// InspectionDetailScreen.js
// Inspection detail view matching the desktop MVIRS inspection screen.
// Read-only for Complete/Voided/Failed/Temp inspections.
// Editable (Apply saves) for Held inspections.
// Arrows navigate to Customer, Vehicle, Insurance sub-screens.

/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const GENERAL_VALUES = ['P', 'N', 'R', 'A', 'F'];
const REG_VALUES = ['P', 'F'];
const VISUAL_VALUES = ['P', 'N', 'R', 'A', 'M', 'X', 'F', '-'];
const BRAKE_TYPE_VALUES = ['B', 'R', 'L'];

const cycleValue = (current, values) => {
  const idx = values.indexOf(current);
  return values[(idx + 1) % values.length];
};

const getResultColor = (value) => {
  switch (value) {
    case 'P': return '#34C759';
    case 'F': return '#E24B4A';
    case 'N': return '#007AFF';
    case 'R': return '#FF9500';
    case 'A': return '#FF9500';
    case 'M': return '#888';
    case 'X': return '#888';
    case '-': return '#ccc';
    default:  return '#ccc';
  }
};

const getBrakeTypeColor = (value) => {
  switch (value) {
    case 'B': return '#007AFF';
    case 'R': return '#FF9500';
    case 'L': return '#444';
    default:  return '#007AFF';
  }
};

const splitBrakeValue = (stored) => {
  if (!stored) return { value: '', type: 'B' };
  const match = stored.match(/^(\d+)([BRL])$/);
  if (match) return { value: match[1], type: match[2] };
  return { value: stored, type: 'B' };
};

const parseRTF = (rtf) => {
  if (!rtf || typeof rtf !== 'string') return '';
  return rtf
    .replace(/\{\\rtf[^}]*\}/g, '')
    .replace(/\{\\fonttbl[^}]*\}/g, '')
    .replace(/\{\\colortbl[^}]*\}/g, '')
    .replace(/\\[a-z]+\d*\s?/g, '')
    .replace(/\{|\}/g, '')
    .replace(/\\\*/g, '')
    .replace(/\\\\/g, '\\')
    .replace(/\\'/g, "'")
    .trim();
};

const toRTF = (plainText) => {
  const DEFAULT_RTF = '{\\rtf1\\ansi\\ansicpg1252\\deff0\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Tahoma;}}\r\n\\viewkind4\\uc1\\pard\\f0\\fs17 \r\n\\par }\r\n';
  if (!plainText || !plainText.trim()) return DEFAULT_RTF;
  return `{\\rtf1\\ansi\\ansicpg1252\\deff0\\deflang1033{\\fonttbl{\\f0\\fnil\\fcharset0 Tahoma;}}\r\n\\viewkind4\\uc1\\pard\\f0\\fs17 ${plainText}\r\n\\par }\r\n`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const statusLabel = (status) => {
  switch (status) {
    case 'C': return 'Complete';
    case 'H': return 'Held';
    case 'V': return 'Voided';
    case 'F': return 'Failed';
    case 'T': return 'Temp';
    default: return status;
  }
};

const typeLabel = (fkType) => {
  switch (fkType) {
    case 1: return 'AI';
    case 2: return 'SI';
    case 3: return 'AO';
    case 4: return 'IM';
    default: return '';
  }
};

const ResultButton = ({ value, onPress, values = GENERAL_VALUES, readOnly, colorFn = getResultColor }) => (
  <button
    style={{
      ...s.resultBtn,
      backgroundColor: colorFn(value),
      cursor: readOnly ? 'default' : 'pointer',
    }}
    onClick={() => !readOnly && onPress(cycleValue(value, values))}
    type="button"
  >
    {value}
  </button>
);

const InspectionRow = ({ label, value, onPress, values, readOnly }) => (
  <div style={s.inspRow}>
    <span style={s.inspLabel}>{label}</span>
    <ResultButton value={value} onPress={onPress} values={values} readOnly={readOnly} />
  </div>
);

export default function InspectionDetailScreen() {
  const { getClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const pkInspection = location.state?.pkInspection || (id && !isNaN(id) ? parseInt(id) : null);
  const historyState = location.state?.historyState;

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);

  const isReadOnly = inspection?.status !== 'H';

  const load = useCallback(async () => {
    if (!pkInspection) { navigate('/dashboard', { state: { historyState } }); return; }
    const client = getClient();
    if (!client) { setError('Not authenticated. Please log in again.'); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await client.get(`/api/inspection/${pkInspection}`);
      const ins = res.data;
      setInspection(ins);

      const lfb = splitBrakeValue(ins.leftFrontBrake);
      const lrb = splitBrakeValue(ins.leftRearBrake);
      const rfb = splitBrakeValue(ins.rightFrontBrake);
      const rrb = splitBrakeValue(ins.rightRearBrake);

      setForm({
        inspectionDate: ins.inspectionDate ? ins.inspectionDate.substring(0, 10) : '',
        workOrder: ins.workOrder || '',
        oldOdometer: ins.oldOdometer || '',
        currentOdometer: ins.currentOdometer || '',
        stickerExpMonth: ins.stickerExpMonth || 0,
        stickerExpYear: ins.stickerExpYear || 0,
        registrationVerified: ins.registrationVerified || 'P',
        tiresAndWheels: ins.tiresAndWheels || 'P',
        steeringAndSuspension: ins.steeringAndSuspension || 'P',
        exhaustSystem: ins.exhaustSystem || 'P',
        fuelSystem: ins.fuelSystem || 'P',
        glazingAndMirrors: ins.glazingAndMirrors || 'P',
        lightsWiringAndSwitches: ins.lightsWiringAndSwitches || 'P',
        bodyDoorsAndLatches: ins.bodyDoorsAndLatches || 'P',
        brakeSystem: ins.brakeSystem || 'P',
        other: ins.other || 'P',
        roadTest: ins.roadTest || 'P',
        leftFrontTire: ins.leftFrontTire?.toString() || '',
        leftRearTire: ins.leftRearTire?.toString() || '',
        rightFrontTire: ins.rightFrontTire?.toString() || '',
        rightRearTire: ins.rightRearTire?.toString() || '',
        leftFrontBrakeValue: lfb.value,
        leftFrontBrakeType: lfb.type,
        leftRearBrakeValue: lrb.value,
        leftRearBrakeType: lrb.type,
        rightFrontBrakeValue: rfb.value,
        rightFrontBrakeType: rfb.type,
        rightRearBrakeValue: rrb.value,
        rightRearBrakeType: rrb.type,
        catalyticConverter: ins.catalyticConverter || '-',
        fuelInletRestrictor: ins.fuelInletRestrictor || '-',
        airPump: ins.airPump || '-',
        evaporativeControlSystem: ins.evaporativeControlSystem || '-',
        pcvValve: ins.pcvValve || '-',
        egrValve: ins.egrValve || '-',
        emissionsStickerNumber: ins.emissionsStickerNumber || '',
        total: ins.totalCostPlusTax?.toFixed(2) || '0.00',
        comments: parseRTF(ins.comments),
        fkEmployee: ins.fkEmployee || 0,
      });
    } catch {
      setError('Could not load inspection.');
    } finally {
      setLoading(false);
    }
  }, [pkInspection, getClient]);

  useEffect(() => { load(); }, []);

  // Apply updated customer/vehicle/insurance from sub-screens
  useEffect(() => {
    if (location.state?.updatedInspection) {
      setInspection(prev => ({ ...prev, ...location.state.updatedInspection }));
    }
  }, [location.state]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleApply = async () => {
    setSaving(true);
    setError('');
    try {
      const client = getClient();
      await client.put(`/api/inspection/${pkInspection}/results`, {
        pkInspection,
        fkEmployee: form.fkEmployee,
        workOrder: form.workOrder,
        inspectionDate: form.inspectionDate,
        oldOdometer: form.oldOdometer,
        currentOdometer: form.currentOdometer,
        registrationVerified: form.registrationVerified,
        tiresAndWheels: form.tiresAndWheels,
        leftFrontTire: parseInt(form.leftFrontTire) || null,
        leftRearTire: parseInt(form.leftRearTire) || null,
        rightFrontTire: parseInt(form.rightFrontTire) || null,
        rightRearTire: parseInt(form.rightRearTire) || null,
        steeringAndSuspension: form.steeringAndSuspension,
        exhaustSystem: form.exhaustSystem,
        fuelSystem: form.fuelSystem,
        glazingAndMirrors: form.glazingAndMirrors,
        lightsWiringAndSwitches: form.lightsWiringAndSwitches,
        bodyDoorsAndLatches: form.bodyDoorsAndLatches,
        brakeSystem: form.brakeSystem,
        other: form.other,
        roadTest: form.roadTest,
        leftFrontBrake: `${form.leftFrontBrakeValue}${form.leftFrontBrakeType}`,
        leftRearBrake: `${form.leftRearBrakeValue}${form.leftRearBrakeType}`,
        rightFrontBrake: `${form.rightFrontBrakeValue}${form.rightFrontBrakeType}`,
        rightRearBrake: `${form.rightRearBrakeValue}${form.rightRearBrakeType}`,
        catalyticConverter: form.catalyticConverter,
        fuelInletRestrictor: form.fuelInletRestrictor,
        airPump: form.airPump,
        evaporativeControlSystem: form.evaporativeControlSystem,
        pcvValve: form.pcvValve,
        egrValve: form.egrValve,
        emissionsStickerNumber: form.emissionsStickerNumber,
        stickerExpMonth: form.stickerExpMonth,
        stickerExpYear: form.stickerExpYear,
        totalCostPlusTax: parseFloat(form.total) || 0,
        comments: toRTF(form.comments),
      });
      navigate('/dashboard', { state: { historyState } });
    } catch {
      setError('Could not save inspection.');
    } finally {
      setSaving(false);
    }
  };

  const goToCustomer = () => navigate('/inspection/customer', {
    state: { pkInspection, fkCustomer: inspection.fkCustomer, fkVehicle: inspection.fkVehicle, isReadOnly, historyState, section: 'customer' }
  });

  const goToVehicle = () => navigate('/inspection/vehicle', {
    state: { pkInspection, fkCustomer: inspection.fkCustomer, fkVehicle: inspection.fkVehicle, isReadOnly, historyState, section: 'vehicle' }
  });

  const goToInsurance = () => navigate('/inspection/insurance', {
    state: { pkInspection, fkCustomer: inspection.fkCustomer, fkInsurance: inspection.fkInsurance, isReadOnly, historyState }
  });

  if (loading) return (
    <div style={s.page}>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.center}><div style={s.spinner} /></div>
    </div>
  );

  if (!inspection || !form) return (
    <div style={s.page}>
      <div style={s.center}><p>{error || 'Inspection not found.'}</p></div>
    </div>
  );


  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerInfo}>
          <div style={s.headerRow}><span style={s.headerLabel}>Sticker No:</span><span style={s.headerValue}>{inspection.stickerNumber}</span></div>
          <div style={s.headerRow}><span style={s.headerLabel}>Inspection No:</span><span style={s.headerValue}>{inspection.inspectionNumber}</span></div>
          <div style={s.headerRow}><span style={s.headerLabel}>Status:</span><span style={s.headerValue}>{statusLabel(inspection.status)}</span></div>
        </div>
        <div style={s.headerLinks}>
          <div style={s.headerLink} onClick={goToCustomer}>
            ▶ {inspection.customerName}, {inspection.customerAddress}
          </div>
          <div style={s.headerLink} onClick={goToVehicle}>
            ▶ {inspection.vehicleYear} {inspection.vehicleMake} {inspection.vehicleModel}, {inspection.vin} (Registered to {inspection.registrantName})
          </div>
          <div style={s.headerLink} onClick={goToInsurance}>
            ▶ {inspection.insuranceCompanyName || 'No Insurance'} {inspection.policyNumber ? `— Policy: ${inspection.policyNumber}` : ''}
          </div>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {/* Body */}
      <div style={s.body}>
        {/* Left column */}
        <div style={s.col}>
          {/* Date, Work Order, Odometers */}
          <div style={s.section}>
            <div style={s.fieldRow}>
              <div style={s.field}>
                <label style={s.label}>Inspection Date</label>
                <input style={s.input} type="date" value={form.inspectionDate} onChange={e => update('inspectionDate', e.target.value)} disabled={isReadOnly} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Work Order</label>
                <input style={s.input} type="text" value={form.workOrder} onChange={e => update('workOrder', e.target.value)} disabled={isReadOnly} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Old Odometer</label>
                <input style={s.input} type="text" value={form.oldOdometer} onChange={e => update('oldOdometer', e.target.value)} disabled={isReadOnly} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Current Odometer</label>
                <input style={s.input} type="text" value={form.currentOdometer} onChange={e => update('currentOdometer', e.target.value)} disabled={isReadOnly} />
              </div>
            </div>
          </div>

          {/* Visual Anti-Tampering */}
          <div style={s.section}>
            <div style={s.sectionTitle}>Visual Anti-Tampering</div>
            <div style={s.twoCol}>
              <div>
                <InspectionRow label="Catalytic Converter" value={form.catalyticConverter} onPress={v => update('catalyticConverter', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Fuel Inlet Restrictor" value={form.fuelInletRestrictor} onPress={v => update('fuelInletRestrictor', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Air Pump" value={form.airPump} onPress={v => update('airPump', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
              </div>
              <div>
                <InspectionRow label="Evaporative Ctl System" value={form.evaporativeControlSystem} onPress={v => update('evaporativeControlSystem', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="PCV Valve" value={form.pcvValve} onPress={v => update('pcvValve', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="EGR Valve" value={form.egrValve} onPress={v => update('egrValve', v)} values={VISUAL_VALUES} readOnly={isReadOnly} />
              </div>
            </div>
          </div>

          {/* General */}
          <div style={s.section}>
            <div style={s.sectionTitle}>General</div>
            <div style={s.twoCol}>
              <div>
                <InspectionRow label="Registration Verified" value={form.registrationVerified} onPress={v => update('registrationVerified', v)} values={REG_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Tires & Wheels" value={form.tiresAndWheels} onPress={v => update('tiresAndWheels', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Steering & Suspension" value={form.steeringAndSuspension} onPress={v => update('steeringAndSuspension', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Exhaust System" value={form.exhaustSystem} onPress={v => update('exhaustSystem', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Fuel System" value={form.fuelSystem} onPress={v => update('fuelSystem', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Glazing & Mirrors" value={form.glazingAndMirrors} onPress={v => update('glazingAndMirrors', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
              </div>
              <div>
                <InspectionRow label="Lights, Wiring & Switches" value={form.lightsWiringAndSwitches} onPress={v => update('lightsWiringAndSwitches', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Body, Doors & Latches" value={form.bodyDoorsAndLatches} onPress={v => update('bodyDoorsAndLatches', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Brake System" value={form.brakeSystem} onPress={v => update('brakeSystem', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Other" value={form.other} onPress={v => update('other', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
                <InspectionRow label="Road Test" value={form.roadTest} onPress={v => update('roadTest', v)} values={GENERAL_VALUES} readOnly={isReadOnly} />
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={s.col}>
          {/* I/M Sticker */}
          <div style={s.section}>
            <div style={s.sectionTitle}>I/M Sticker</div>
            <input
              style={s.input}
              type="text"
              value={form.emissionsStickerNumber}
              onChange={e => update('emissionsStickerNumber', e.target.value)}
              disabled={isReadOnly}
              placeholder="IMx-xxxxxxx"
            />
          </div>

          {/* Tires */}
          <div style={s.section}>
            <div style={s.sectionTitle}>Tires (32nds)</div>
            <div style={s.tireGrid}>
              {[
                { label: 'Left Front', field: 'leftFrontTire' },
                { label: 'Right Front', field: 'rightFrontTire' },
                { label: 'Left Rear', field: 'leftRearTire' },
                { label: 'Right Rear', field: 'rightRearTire' },
              ].map(t => (
                <div key={t.field} style={s.tireCell}>
                  <label style={s.label}>{t.label}</label>
                  <input style={s.tireInput} type="text" value={form[t.field]} onChange={e => update(t.field, e.target.value)} disabled={isReadOnly} maxLength={2} />
                </div>
              ))}
            </div>
          </div>

          {/* Brakes */}
          <div style={s.section}>
            <div style={s.sectionTitle}>Brakes</div>
            <div style={s.tireGrid}>
              {[
                { label: 'Left Front', vf: 'leftFrontBrakeValue', tf: 'leftFrontBrakeType' },
                { label: 'Right Front', vf: 'rightFrontBrakeValue', tf: 'rightFrontBrakeType' },
                { label: 'Left Rear', vf: 'leftRearBrakeValue', tf: 'leftRearBrakeType' },
                { label: 'Right Rear', vf: 'rightRearBrakeValue', tf: 'rightRearBrakeType' },
              ].map(b => (
                <div key={b.label} style={s.tireCell}>
                  <label style={s.label}>{b.label}</label>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input style={s.tireInput} type="text" value={form[b.vf]} onChange={e => update(b.vf, e.target.value)} disabled={isReadOnly} maxLength={2} />
                    <ResultButton value={form[b.tf]} onPress={v => update(b.tf, v)} values={BRAKE_TYPE_VALUES} readOnly={isReadOnly} colorFn={getBrakeTypeColor} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sticker Exp + Comments + Total */}
          <div style={s.section}>
            <div style={s.fieldRow}>
              <div style={s.field}>
                <label style={s.label}>Sticker Exp Month/Year</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...s.input, width: 60 }} type="text" value={form.stickerExpMonth ? String(form.stickerExpMonth).padStart(2, '0') : ''} onChange={e => update('stickerExpMonth', parseInt(e.target.value) || 0)} disabled={isReadOnly} maxLength={2} placeholder="MM" />
                  <input style={{ ...s.input, width: 70 }} type="text" value={form.stickerExpYear || ''} onChange={e => update('stickerExpYear', parseInt(e.target.value) || 0)} disabled={isReadOnly} maxLength={4} placeholder="YYYY" />
                </div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Total Cost</label>
                <input style={s.input} type="text" value={form.total} onChange={e => update('total', e.target.value)} disabled={isReadOnly} />
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Comments</label>
              <textarea style={{ ...s.input, height: 80, resize: 'vertical', fontFamily: 'inherit' }} value={form.comments} onChange={e => update('comments', e.target.value)} disabled={isReadOnly} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <div style={s.footerLeft}>
          <span style={s.footerInfo}>
            {typeLabel(inspection.fkInspectionType)} — {statusLabel(inspection.status)} — {formatDate(inspection.inspectionDate)}
          </span>
        </div>
        <div style={s.footerRight}>
          {!isReadOnly && (
            <button style={s.applyBtn} onClick={handleApply} disabled={saving}>
              {saving ? 'Saving...' : 'Apply'}
            </button>
          )}
          <button style={s.closeBtn} onClick={() => navigate('/dashboard', { state: { historyState } })}>Close</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:         { minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f0', fontFamily: 'system-ui, sans-serif', fontSize: 13 },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, padding: 40 },
  spinner:      { width: 36, height: 36, border: '4px solid #e0e0e0', borderTop: '4px solid #007AFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  error:        { padding: '8px 12px', backgroundColor: '#ffe0e0', color: '#cc0000', fontSize: 13 },
  header:       { backgroundColor: '#e8f0f8', borderBottom: '2px solid #bcd', padding: '8px 12px', display: 'flex', gap: 24, flexWrap: 'wrap' },
  headerInfo:   { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 180 },
  headerRow:    { display: 'flex', gap: 8, alignItems: 'center' },
  headerLabel:  { fontWeight: 700, color: '#333', fontSize: 13 },
  headerValue:  { color: '#1a1a1a', fontSize: 13 },
  headerLinks:  { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  headerLink:   { color: '#007AFF', cursor: 'pointer', fontSize: 13, padding: '2px 0' },
  body:         { display: 'flex', flex: 1, gap: 0, overflow: 'auto' },
  col:          { flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  section:      { backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' },
  sectionTitle: { fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 },
  fieldRow:     { display: 'flex', gap: 10, flexWrap: 'wrap' },
  field:        { display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 100 },
  label:        { fontSize: 12, color: '#666', fontWeight: 600 },
  input:        { padding: '4px 6px', border: '1px solid #ccc', borderRadius: 3, fontSize: 13, backgroundColor: '#fafafa', outline: 'none' },
  twoCol:       { display: 'flex', gap: 16 },
  inspRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 },
  inspLabel:    { fontSize: 13, color: '#333', flex: 1 },
  resultBtn:    { width: 34, height: 28, border: 'none', borderRadius: 4, color: '#fff', fontWeight: 700, fontSize: 13 },
  tireGrid:     { display: 'flex', flexWrap: 'wrap', gap: 10 },
  tireCell:     { width: 'calc(50% - 5px)', display: 'flex', flexDirection: 'column', gap: 3 },
  tireInput:    { padding: '4px 6px', border: '1px solid #ccc', borderRadius: 3, fontSize: 15, fontWeight: 700, textAlign: 'center', width: 50, backgroundColor: '#fafafa' },
  footer:       { backgroundColor: '#e8e8e8', borderTop: '2px solid #bbb', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  footerLeft:   { display: 'flex', alignItems: 'center' },
  footerRight:  { display: 'flex', gap: 8 },
  footerInfo:   { fontSize: 13, color: '#555' },
  applyBtn:     { padding: '5px 20px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  closeBtn:     { padding: '5px 20px', backgroundColor: '#fff', color: '#333', border: '1px solid #bbb', borderRadius: 4, fontSize: 14, cursor: 'pointer' },
};